import { useCallback } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CircularProgress } from "@/components/monitoring/CircularProgress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import { formatBytes, formatDuration } from "@/lib/chart-helpers";

type BackendSample = {
  ts: number;
  cpu: { user: number; system: number; percent: number };
  memory: { rss: number; heapUsed: number; heapTotal: number; external: number; percentUsed: number };
  disk: { total: number; free: number; used: number; percentUsed: number };
  uptime: number;
  pid: number;
  eventLoopLag: number | null;
};

type LatencyStat = {
  method: string;
  count: number;
  p50: number;
  p95: number;
  p99: number;
  errorRate: number;
};

type PerfData = {
  samples: BackendSample[];
  latency: LatencyStat[];
};

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function Performance() {
  const { rpc, state } = useGatewayRpc();

  const fetcher = useCallback(async (): Promise<PerfData> => {
    const [sys, lat] = await Promise.all([
      rpc<{ samples: BackendSample[] }>("metrics.system", { since: Date.now() - 3_600_000 }),
      rpc<{ stats: LatencyStat[] }>("metrics.latency", { since: Date.now() - 3_600_000 }),
    ]);
    return { samples: sys.samples, latency: lat.stats };
  }, [rpc]);

  const { data } = usePolling(fetcher, 10_000, { enabled: state === "connected" });

  const samples = data?.samples ?? [];
  const latency = data?.latency ?? [];

  // Aggregate latency for gauges
  const allP50 = latency.length > 0 ? latency.reduce((s, l) => s + l.p50, 0) / latency.length : 0;
  const allP95 = latency.length > 0 ? latency.reduce((s, l) => s + l.p95, 0) / latency.length : 0;
  const allP99 = latency.length > 0 ? latency.reduce((s, l) => s + l.p99, 0) / latency.length : 0;
  const totalRequests = latency.reduce((s, l) => s + l.count, 0);
  const avgErrorRate = latency.length > 0 ? latency.reduce((s, l) => s + l.errorRate, 0) / latency.length : 0;

  // Chart data
  const cpuChart = samples.map((s) => ({ ts: s.ts, time: fmtTime(s.ts), cpu: s.cpu.percent }));
  const memChart = samples.map((s) => ({
    ts: s.ts,
    time: fmtTime(s.ts),
    rss: s.memory.rss / (1024 * 1024),
    heap: s.memory.heapUsed / (1024 * 1024),
    external: s.memory.external / (1024 * 1024),
  }));
  const elChart = samples
    .filter((s) => s.eventLoopLag != null)
    .map((s) => ({ ts: s.ts, time: fmtTime(s.ts), lag: s.eventLoopLag! }));

  // Latency gauge normalized to 0-100 (100ms = 100%)
  const p50Gauge = Math.min(100, (allP50 / 100) * 100);
  const p95Gauge = Math.min(100, (allP95 / 200) * 100);
  const p99Gauge = Math.min(100, (allP99 / 500) * 100);

  if (state !== "connected" && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Performance</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Performance</h1>

      {/* Latency Gauges + Throughput */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card>
          <CardContent className="flex flex-col items-center pt-6">
            <CircularProgress value={p50Gauge} label="p50" />
            <span className="mt-1 text-xs text-muted-foreground">{allP50.toFixed(0)}ms</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex flex-col items-center pt-6">
            <CircularProgress value={p95Gauge} label="p95" />
            <span className="mt-1 text-xs text-muted-foreground">{allP95.toFixed(0)}ms</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex flex-col items-center pt-6">
            <CircularProgress value={p99Gauge} label="p99" />
            <span className="mt-1 text-xs text-muted-foreground">{allP99.toFixed(0)}ms</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Throughput</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-semibold tabular-nums">{totalRequests}</span>
            <span className="ml-1 text-sm text-muted-foreground">reqs/1h</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Error Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <span className="text-2xl font-semibold tabular-nums">{(avgErrorRate * 100).toFixed(1)}%</span>
          </CardContent>
        </Card>
      </div>

      {/* CPU Time Series */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">CPU Usage (1h)</CardTitle>
        </CardHeader>
        <CardContent>
          {cpuChart.length < 2 ? (
            <p className="py-8 text-center text-sm text-slate-400">Collecting data...</p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={cpuChart}>
                <defs>
                  <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-muted)" opacity={0.2} />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="var(--color-muted)" />
                <YAxis tick={{ fontSize: 10 }} stroke="var(--color-muted)" unit="%" />
                <Tooltip formatter={(v: number) => [`${v.toFixed(1)}%`, "CPU"]} />
                <Area type="monotone" dataKey="cpu" stroke="#6366f1" fill="url(#cpuGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Memory Time Series */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Memory Usage (1h)</CardTitle>
        </CardHeader>
        <CardContent>
          {memChart.length < 2 ? (
            <p className="py-8 text-center text-sm text-slate-400">Collecting data...</p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={memChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-muted)" opacity={0.2} />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="var(--color-muted)" />
                <YAxis tick={{ fontSize: 10 }} stroke="var(--color-muted)" tickFormatter={(v: number) => `${v.toFixed(0)}MB`} />
                <Tooltip formatter={(v: number) => [`${v.toFixed(1)} MB`]} />
                <Area type="monotone" dataKey="rss" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} name="RSS" />
                <Area type="monotone" dataKey="heap" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} name="Heap" />
                <Area type="monotone" dataKey="external" stroke="#22c55e" fill="#22c55e" fillOpacity={0.1} name="External" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Event Loop Lag */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Event Loop Lag (1h)</CardTitle>
        </CardHeader>
        <CardContent>
          {elChart.length < 2 ? (
            <p className="py-8 text-center text-sm text-slate-400">Collecting data...</p>
          ) : (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={elChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-muted)" opacity={0.2} />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="var(--color-muted)" />
                <YAxis tick={{ fontSize: 10 }} stroke="var(--color-muted)" unit="ms" />
                <Tooltip formatter={(v: number) => [`${v.toFixed(2)}ms`, "Lag"]} />
                <Line type="monotone" dataKey="lag" stroke="#a855f7" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Per-Method Latency Table */}
      {latency.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Per-Method Latency</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700">
                  <th className="pb-2 pr-4">Method</th>
                  <th className="pb-2 pr-4 text-right">Count</th>
                  <th className="pb-2 pr-4 text-right">p50</th>
                  <th className="pb-2 pr-4 text-right">p95</th>
                  <th className="pb-2 pr-4 text-right">p99</th>
                  <th className="pb-2 text-right">Errors</th>
                </tr>
              </thead>
              <tbody>
                {latency
                  .slice()
                  .sort((a, b) => b.count - a.count)
                  .map((l) => (
                    <tr key={l.method} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-2 pr-4 font-mono text-xs">{l.method}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{l.count}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{l.p50.toFixed(0)}ms</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{l.p95.toFixed(0)}ms</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{l.p99.toFixed(0)}ms</td>
                      <td className="py-2 text-right tabular-nums">{(l.errorRate * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
